import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import IconCross from './IconCross.js';
import IconExternalLink from './IconExternalLink.js';
import IconLink from './IconLink.js';
import IconMaximize from './IconMaximize.js';
import IconError from './IconError.js';
import { G as Group } from './Group-ba54dd0f.js';
import { T as Tooltip } from './Tooltip-75eb30cd.js';
import useModalState from './useModalState.js';
import useModalPortal from './useModalPortal.js';
import useCopyToClipboard from './useCopyToClipboard.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { O as Overlay } from './Overlay-c16fe126.js';
import { R as Responsive } from './Responsive-28d2e028.js';
import { B as Button } from './Button-7604c54d.js';

// import './Overlay.css';
/**
Component to display an Overlay
*/

function InnerBrowserButton({
  icon: Icon,
  href,
  tooltip,
  isSmall,
  disabled,
  error,
  success,
  ...linkProps
}) {
  const Component = href ? 'a' : 'button';
  const modifiedLinkProps = {
    ...linkProps
  };
  if (Component === 'button') modifiedLinkProps.type = 'button';
  if (href) modifiedLinkProps.href = href;
  let tooltipContent = /*#__PURE__*/React.createElement(Group, {
    gap: "xs",
    wrap: false
  }, tooltip);
  const tooltipCss = ['st_ibm_tooltip'];
  if (error) {
    tooltipCss.push('st_ibm_tooltip--error');
    tooltipContent = /*#__PURE__*/React.createElement(Group, {
      gap: "xs",
      wrap: false
    }, /*#__PURE__*/React.createElement(IconError, null), /*#__PURE__*/React.createElement(Group.Item, null, error));
  }
  if (success) {
    tooltipCss.push('st_ibm_tooltip--success');
    tooltipContent = /*#__PURE__*/React.createElement(Group, {
      gap: "xs",
      wrap: false
    }, success);
  }
  return /*#__PURE__*/React.createElement(Tooltip, {
    type: error || success ? 'permanent' : 'default'
  }, /*#__PURE__*/React.createElement(Tooltip.Content, {
    className: tooltipCss.join(' '),
    position: isSmall ? 'bottom' : 'left',
    id: `tooltipContent_${tooltip.replace(/ /g, '-')}`
  }, tooltipContent), /*#__PURE__*/React.createElement(Tooltip.Trigger, {
    "aria-describedby": `tooltipContent_${tooltip.replace(/ /g, '-')}`
  }, /*#__PURE__*/React.createElement(Component, _extends({
    className: "st_ibm_btn",
    disabled: disabled
  }, modifiedLinkProps), Icon && /*#__PURE__*/React.createElement(Icon, {
    size: "md"
  }))));
}
InnerBrowserButton.propTypes = {
  /** icon to display */
  icon: PropTypes.string.isRequired,
  /** href for the button */
  href: PropTypes.string,
  /** tooltip for the button */
  tooltip: PropTypes.string.isRequired,
  /** is the button small */
  isSmall: PropTypes.bool,
  /** is the button disabled */
  disabled: PropTypes.bool,
  /** error message to display */
  error: PropTypes.string,
  /** success message to display */
  success: PropTypes.string
};
InnerBrowserButton.defaultProps = {
  href: undefined,
  isSmall: false,
  disabled: false,
  error: undefined,
  success: undefined
};
InnerBrowserButton.displayName = 'InnerBrowserButton';

/**
Component to display an Overlay
*/
function InnerBrowserModel({
  href,
  icon,
  trigger,
  target,
  children,
  className
}) {
  const [value, copy, supported, error] = useCopyToClipboard();
  const {
    renderNode
  } = useModalPortal();
  const {
    open,
    openModal,
    closeModal
  } = useModalState();
  let cssClasses = ['st_ibm_content_wrapper_group'];
  cssClasses = addClassNames(className, cssClasses);
  const isTriggerString = typeof trigger === 'string';
  const TriggerComponent = isTriggerString ? Button : trigger;
  const triggerProps = {};
  if (icon) triggerProps.icon = icon;
  if (isTriggerString) {
    triggerProps.children = trigger;
    triggerProps.tertiary = true;
  }
  if (!href && !children) {
    throw new Error('InnerBrowserModel requires either a href or children prop');
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TriggerComponent, _extends({}, triggerProps, {
    onClick: () => openModal()
  })), open && /*#__PURE__*/React.createElement(Overlay, {
    renderNode: renderNode,
    status: "open",
    className: "st_ibm_content_wrapper"
  }, /*#__PURE__*/React.createElement(Responsive, {
    xs: true
  }, isSmall => {
    const contentCss = ['st_ibm_content'];
    if (isSmall) {
      contentCss.push('st_ibm_content--mobile');
    }
    return /*#__PURE__*/React.createElement(Group, {
      valign: "start",
      direction: isSmall ? 'column-reverse' : 'row',
      className: cssClasses,
      gap: "sm",
      align: isSmall ? 'end' : 'start'
    }, children ? /*#__PURE__*/React.createElement("div", {
      className: addClassNames(className, contentCss).join(' ')
    }, children) : /*#__PURE__*/React.createElement("iframe", {
      src: href,
      className: addClassNames(className, contentCss).join(' '),
      title: "Inner Browser"
    }), /*#__PURE__*/React.createElement(Group, {
      direction: isSmall ? 'row-reverse' : 'column',
      gap: "sm"
    }, /*#__PURE__*/React.createElement(InnerBrowserButton, {
      onClick: () => closeModal(),
      isSmall: isSmall,
      icon: IconCross,
      tooltip: "Close"
    }), href && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InnerBrowserButton, {
      href: href,
      icon: IconMaximize,
      isSmall: isSmall,
      tooltip: "Open in current window",
      target: target
    }), /*#__PURE__*/React.createElement(InnerBrowserButton, {
      href: href,
      target: "_blank",
      rel: "noreferrer",
      onClick: () => {
        closeModal();
      },
      icon: IconExternalLink,
      isSmall: isSmall,
      tooltip: "Open in new tab"
    }), /*#__PURE__*/React.createElement(InnerBrowserButton, {
      onClick: e => {
        e.preventDefault();
        copy(href);
      },
      icon: IconLink,
      isSmall: isSmall,
      tooltip: supported ? 'Copy URL to clipboard' : 'Clipboard not supported',
      disabled: !supported,
      error: error,
      success: value ? `Copied ${value}` : null
    }))));
  })));
}
InnerBrowserModel.propTypes = {
  /**
   * Optional: The content of the InnerBrowserModel, if children are not provided,
   * an iframe will be rendered with the href prop as the src
   */
  children: PropTypes.node,
  /**
   * The href of the page to display in the InnerBrowserModel,
   * if not provided, the children will be required
   */
  href: PropTypes.string,
  /**
   * The target for the href link
   */
  target: PropTypes.oneOf(['_self', '_top', '_parent']),
  /**
   * The icon to display in the trigger button
   */
  icon: PropTypes.any,
  /**
   * The trigger component to open InnerBrowserModel, defaults to Button
   */
  trigger: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  className: classNamePropTypes
};
InnerBrowserModel.defaultProps = {
  children: null,
  href: null,
  icon: null,
  trigger: Button,
  target: '_self',
  className: null
};
InnerBrowserModel.displayName = 'InnerBrowserModel';

export { InnerBrowserModel as I };
